import { useCallback, useState } from 'react';

const VALID_LIMITS = [5, 10, 25, 50, 100];

const readStoredLimit = (key, fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || !VALID_LIMITS.includes(parsed)) return fallback;
    return parsed;
  } catch (_err) {
    return fallback;
  }
};

/**
 * Persistent rows-per-page state for BO tables.
 *
 * Stores the chosen page-size in localStorage under `bo:rowsPerPage:<key>`
 * so that on reload the same value is restored. Page number itself is
 * intentionally NOT persisted — reloading should land you on page 1.
 *
 * Usage:
 *   const [pagerInfo, setPagerInfo] = usePersistedPagerInfo('washers', { limit: 10 });
 */
export const usePersistedPagerInfo = (key, { limit = 10 } = {}) => {
  const storageKey = `bo:rowsPerPage:${key}`;
  const initialLimit = readStoredLimit(storageKey, limit);

  const [pagerInfo, _setPagerInfo] = useState({
    limit: initialLimit,
    page: 1,
    totalSize: 0,
  });

  const setPagerInfo = useCallback(
    (next) => {
      _setPagerInfo((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        if (resolved && resolved.limit && resolved.limit !== prev.limit) {
          try {
            window.localStorage.setItem(storageKey, String(resolved.limit));
          } catch (_err) {
            /* localStorage quota / privacy mode — non-fatal */
          }
        }
        return resolved;
      });
    },
    [storageKey],
  );

  return [pagerInfo, setPagerInfo];
};

export default usePersistedPagerInfo;
